import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { DemographicsForm, DemographicsData } from "@/components/onboarding/DemographicsForm";
import { AuthLogo } from "@/components/auth/AuthLogo";
import { toast } from "@/hooks/use-toast";

const Onboarding = () => {
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [existingData, setExistingData] = useState<Partial<DemographicsData> | null>(null);
  
  const returnTo = searchParams.get("returnTo") || "/dashboard";

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/auth/login");
        return;
      }
      checkOnboardingStatus();
    }
  }, [user, authLoading]);

  const checkOnboardingStatus = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from("user_demographics")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;

      if (data?.onboarding_completed || data?.onboarding_skipped) {
        // Already completed or skipped, redirect
        navigate(returnTo);
        return;
      }

      if (data) {
        // Has partial data
        setExistingData({
          date_of_birth: data.date_of_birth ? new Date(data.date_of_birth) : null,
          gender_identity: data.gender_identity,
          sexual_orientation: data.sexual_orientation,
          neurodivergences: data.neurodivergences || [],
          ethnicity: data.ethnicity,
          work_status: data.work_status,
          disabilities: data.disabilities || [],
        });
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data: DemographicsData) => {
    if (!user) return;

    try {
      const demographicsPayload = {
        user_id: user.id,
        date_of_birth: data.date_of_birth 
          ? `${data.date_of_birth.getFullYear()}-${String(data.date_of_birth.getMonth() + 1).padStart(2, '0')}-${String(data.date_of_birth.getDate()).padStart(2, '0')}`
          : null,
        gender_identity: data.gender_identity,
        sexual_orientation: data.sexual_orientation,
        neurodivergences: data.neurodivergences.length > 0 ? data.neurodivergences : null,
        ethnicity: data.ethnicity,
        work_status: data.work_status,
        disabilities: data.disabilities.length > 0 ? data.disabilities : null,
        onboarding_completed: true,
        onboarding_skipped: false,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from("user_demographics")
        .upsert(demographicsPayload, { onConflict: "user_id" });

      if (error) throw error;

      toast({
        title: t("onboarding.saveSuccess"),
        variant: "success",
      });

      navigate(returnTo);
    } catch (error: any) {
      console.error("Error saving demographics:", error);
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "error",
      });
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("user_demographics")
        .upsert({
          user_id: user.id,
          onboarding_completed: false,
          onboarding_skipped: true,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id" });

      if (error) throw error;

      navigate(returnTo);
    } catch (error: any) {
      console.error("Error skipping onboarding:", error);
      navigate(returnTo);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <BrutalCard className="w-full max-w-lg">
        <div className="text-center mb-6">
          <AuthLogo />
          <h1 className="font-heading font-bold text-2xl text-dark mb-2">
            {t("onboarding.title")}
          </h1>
          <p className="text-muted-foreground font-body text-sm">
            {t("onboarding.subtitle")}
          </p>
        </div>

        {/* Privacy Note */}
        <div className="bg-surface border-2 border-dark p-4 mb-6 flex items-start gap-3">
          <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            {t("onboarding.privacyNote")}
          </p>
        </div>

        <DemographicsForm
          initialData={existingData || undefined}
          onSave={handleSave}
          onSkip={handleSkip}
          showSkipButton={true}
        />
      </BrutalCard>
    </div>
  );
};

export default Onboarding;
